import { zodResolver } from "@hookform/resolvers/zod";
import type { FieldError, NestedValue, UseFormProps } from "react-hook-form";
import { useForm as useDefaultForm } from "react-hook-form";
import type { z } from "zod";
import type { Maybe, Scalars } from "~/gql/graphql";
import { dayjs } from "../dayjs";
import { errorMap } from "../zod";

type NestedValues<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Schema extends z.ZodEffects<any> | z.ZodObject<any>,
  K extends keyof z.infer<Schema>
> = {
  [k in K]: NestedValue<z.infer<Schema>[K]>;
};

const useForm = <
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Schema extends z.ZodEffects<any> | z.ZodObject<any>,
  // eslint-disable-next-line @typescript-eslint/sort-type-union-intersection-members
  SchemaKeys extends void | keyof z.infer<Schema> = void
>(
  schema: Schema,
  options?: Omit<
    // eslint-disable-next-line @typescript-eslint/sort-type-union-intersection-members
    UseFormProps<
      SchemaKeys extends keyof z.infer<Schema>
        ? // eslint-disable-next-line @typescript-eslint/sort-type-union-intersection-members
          z.infer<Schema> & NestedValues<Schema, SchemaKeys>
        : z.infer<Schema>
    >,
    "resolver"
  >
) => {
  // eslint-disable-next-line @typescript-eslint/sort-type-union-intersection-members
  return useDefaultForm<
    SchemaKeys extends keyof z.infer<Schema>
      ? // eslint-disable-next-line @typescript-eslint/sort-type-union-intersection-members
        z.infer<Schema> & NestedValues<Schema, SchemaKeys>
      : z.infer<Schema>
  >({
    resolver: zodResolver(schema, { errorMap }),
    ...options,
  });
};

const nullable = (v: string) => (v === "" ? null : v);

const nullableDate = (v: string | null | undefined) => {
  if (!v || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return null;

  const d = dayjs(v);
  if (!d.isValid()) return null;

  const dt = new Date(0);
  dt.setHours(0, 0, 0, 0);
  dt.setFullYear(d.year(), d.month(), d.date());
  return dt;
};

const nullableFloat = (v: string) =>
  v === "" ? null : isNaN(parseFloat(v)) ? null : parseFloat(v);

/*
 ** Convert a timestamptz string coming from our graphql api into
 ** a value ready to be consumed by an <input type="date" />
 ** ex: ("2021-01-01T00:00:00+02:00" -> "2021-01-01")
 */
const timestamptzToDateValue = (
  timestamptz?: Maybe<Scalars["timestamptz"]["output"]>
): Date | undefined => {
  if (timestamptz) {
    const d = dayjs(timestamptz);
    const dt = new Date(0);
    dt.setHours(0, 0, 0, 0);
    dt.setFullYear(d.year(), d.month(), d.date());
    return dt;
  }
  return undefined;
};

const timestamptzToTimeValue = (
  timestamptz?: Maybe<Scalars["timestamptz"]["output"]>
) => {
  if (timestamptz) {
    const time = dayjs(timestamptz).format("HH:mm");
    return time;
  }

  return undefined;
};

const timestamptzToDatetimeValue = (
  timestamptz?: Maybe<Scalars["timestamptz"]["output"]>
): string | undefined => {
  if (timestamptz) {
    return dayjs(timestamptz).format("YYYY-MM-DDTHH:mm");
  }
  return undefined;
};

export * from "react-hook-form";

export type { FieldError, NestedValues };
export {
  nullable,
  nullableDate,
  nullableFloat,
  timestamptzToDatetimeValue,
  timestamptzToDateValue,
  timestamptzToTimeValue,
  useForm,
};
